// Mirrors main.py:_AIME_HOSTS. Single source of truth for "is this page being served on a
// customer custom domain rather than on the AIME app host". Used to decide whether the browser's
// current window.location.hostname should render the admin app (AIME host) or the public
// booking routes (a customer's connected custom domain).
//
// This is a MIRROR, not a shared source — the frontend cannot import main.py's Python set. If a
// new AIME hostname is ever added to main.py:_AIME_HOSTS OR to local_settings.py's
// AIME_EXTRA_HOSTS (local_settings.py is per-box, untracked in git — check it directly on the
// target server, don't assume main.py's tracked source is the whole story), it must also be
// added here, or added to REACT_APP_AIME_HOSTS at build time. See Known Risk #13 in
// specs/completed/booking-custom-domain-routing.md, and I1 in
// specs/completed/booking-custom-domain-routing-code-review.md (iteration 1) for a real instance
// of this drift shipping silently — the genesis.clearbyte.au hosts below were added to close it.

// Keep in sync with main.py:_AIME_HOSTS' EFFECTIVE set — that is the 4-host base literal
// UNIONED with local_settings.AIME_EXTRA_HOSTS (main.py:241-246), NOT just the base literal.
// local_settings.py is not tracked in git and lives only on each box, so check it directly
// (e.g. `cat local_settings.py` on the target server) rather than assuming main.py's source
// alone tells the whole story. Production Helsinki's local_settings.py currently adds
// genesis.clearbyte.au / pages.genesis.clearbyte.au on top of the base 4 below.
const AIME_HOSTS = new Set<string>([
  'localhost',
  '127.0.0.1',
  'aime.clearbyte.au',
  'pages.aime.clearbyte.au',
  // AIME staging box — nginx.conf SNI map + local_settings.AIME_EXTRA_HOSTS on production Helsinki
  'genesis.clearbyte.au',
  'pages.genesis.clearbyte.au',
]);

// Escape hatch for hosts that can't be hardcoded here (mirrors local_settings.AIME_EXTRA_HOSTS on
// the backend, which the frontend build cannot read). Comma-separated, set at build time.
const EXTRA_HOSTS: string[] = (process.env.REACT_APP_AIME_HOSTS || '')
  .split(',')
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);

for (const h of EXTRA_HOSTS) {
  AIME_HOSTS.add(h);
}

/** True for a bare IPv4 literal, e.g. "10.0.0.25". */
const isIpv4Literal = (host: string): boolean => /^\d{1,3}(\.\d{1,3}){3}$/.test(host);

/** True for a bare IPv6 literal (hostnames wrap these in brackets, e.g. "[::1]"). */
const isIpv6Literal = (host: string): boolean => host.startsWith('[') && host.endsWith(']');

/** True for any "*.local" hostname (mDNS / local dev). */
const isDotLocal = (host: string): boolean => host.endsWith('.local');

/**
 * True only when the current browser hostname is NEITHER an AIME app host, an IP literal, nor a
 * `.local` host — i.e. it is a customer's connected custom domain. Hostname cannot change during
 * a page's lifetime, so callers should compute this once at module scope, not per render.
 */
export const isPublicCustomDomainHost = (): boolean => {
  const host = (window.location.hostname || '').trim().toLowerCase();
  if (!host) return false;
  if (AIME_HOSTS.has(host)) return false;
  if (isIpv4Literal(host) || isIpv6Literal(host) || isDotLocal(host)) return false;
  // Loud-drift guard: every known AIME-operated host lives under clearbyte.au. A NEW
  // "*.clearbyte.au" host reaching here (not in AIME_HOSTS, not covered by
  // REACT_APP_AIME_HOSTS) is far more likely to be a missed mirror update than a genuine
  // customer domain — warn once so the gap surfaces on that host's own console instead of
  // silently hiding admin routes, per code review I1 (mirror drift was previously silent).
  if (host.endsWith('.clearbyte.au')) {
    // eslint-disable-next-line no-console
    console.warn(
      `[publicHost] "${host}" looks like an AIME-operated host but is not in publicHost.ts's ` +
        'AIME_HOSTS mirror or REACT_APP_AIME_HOSTS. If this is a genuine AIME app host (not a ' +
        'customer domain), add it to frontend/src/utils/publicHost.ts and check local_settings.py ' +
        "on this box's backend for the authoritative AIME_EXTRA_HOSTS set."
    );
  }
  return true;
};
